/**
 * Supabase side of the ADR 0034 screens: battery / lift / eco reports (SECURITY INVOKER, RLS applies; files
 * through export_report, which logs them), the Teltonika sync status and the vehicle analog calibration.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  batteryTotals,
  type BatteryResult,
  type BatterySource,
  type DeviceFamily,
  type DrivingRowKind,
  type EcoEventType,
  type EcoResult,
  type ExportFormat,
  type ImmobilizerState,
  type LiftResult,
  type LiftSource,
  type VehicleAnalogCalibration,
} from "@fts/core";
import type { AnalogCalibrationRecord, DeviceImmobilizer, TeltonikaSyncStatus } from "./provider";
import type { BatteryQuery, TelematicsQuery } from "./reports";

type PgError = { message: string; code?: string };
type Res<T> = { data: T | null; error: PgError | null };
type Check = <T>(res: Res<T>) => T;
type Num = number | string;
const n = (v: Num | null | undefined) => (v === null || v === undefined ? 0 : Number(v));
const nn = (v: Num | null | undefined) => (v === null || v === undefined ? null : Number(v));

/* ---------------- battery ---------------- */

type BatteryJson = {
  tz: string; bucket_s: Num;
  rules: { min_s: Num; opportunity_max_s: Num; hold_s: Num; settle_s: Num; median_n: Num; level_v_per_cell: Num; end_v_per_cell: Num };
  vehicles: {
    vehicle_id: string; fleet_no: string; family: DeviceFamily | null; source: BatterySource; low_v: Num | null; rise_v: Num; level_v: Num | null;
    readings: Num; min_v: Num | null; max_v: Num | null; avg_v: Num | null; low_readings: Num; low_alarms: Num;
    series: [string, Num, Num, Num][];
    sessions: { start: string; end: string; dur_s: Num; start_v: Num; end_v: Num; peak_v: Num; opportunity: boolean; open: boolean }[];
  }[];
};

export function toBattery(r: BatteryJson): BatteryResult {
  const vehicles = (r.vehicles ?? []).map((v) => ({
    vehicleId: v.vehicle_id, fleetNo: v.fleet_no, family: v.family, source: v.source, readings: n(v.readings),
    minV: nn(v.min_v), maxV: nn(v.max_v), avgV: nn(v.avg_v), lowV: nn(v.low_v), lowReadings: n(v.low_readings), lowAlarms: n(v.low_alarms),
    riseV: n(v.rise_v), levelV: nn(v.level_v),
    series: (v.series ?? []).map(([at, avg, mn, mx]) => ({ at, avgV: n(avg), minV: n(mn), maxV: n(mx) })),
    sessions: (v.sessions ?? []).map((s) => ({ start: s.start, end: s.end, durS: n(s.dur_s), startV: n(s.start_v), endV: n(s.end_v), peakV: n(s.peak_v), opportunity: s.opportunity, open: s.open })),
  }));
  return {
    tz: r.tz, bucketS: n(r.bucket_s),
    rules: {
      minS: n(r.rules?.min_s), opportunityMaxS: n(r.rules?.opportunity_max_s), holdS: n(r.rules?.hold_s), settleS: n(r.rules?.settle_s),
      medianN: n(r.rules?.median_n), levelVPerCell: n(r.rules?.level_v_per_cell), endVPerCell: n(r.rules?.end_v_per_cell),
    },
    vehicles, totals: batteryTotals(vehicles),
  };
}

export const batteryArgs = (q: BatteryQuery) => ({ tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, from: q.from, to: q.to });

export async function reportBattery(db: SupabaseClient, check: Check, q: BatteryQuery): Promise<BatteryResult> {
  return toBattery(check<BatteryJson>(await db.rpc("report_battery", { p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null, p_from: q.from, p_to: q.to })));
}
export async function exportBattery(db: SupabaseClient, check: Check, q: BatteryQuery, format: ExportFormat): Promise<BatteryResult> {
  return toBattery(check<BatteryJson>(await db.rpc("export_report", { p_report: "battery", p_format: format, p_args: batteryArgs(q) })));
}

/* ---------------- lift / eco ---------------- */

type RowBase = { kind: DrivingRowKind; id: string | null; name: string | null; cards: Num; vehicles: Num | null; suppressed: boolean };
type LiftJson = {
  tz: string; group: LiftResult["group"];
  rows: (RowBase & { lifts: Num | null; lift_s: Num | null; while_driving: Num | null; while_driving_s: Num | null })[];
  totals: { lifts: Num; lift_s: Num; while_driving: Num; while_driving_s: Num; vehicles: Num; operators: Num };
  unconfigured: { vehicle_id: string; fleet_no: string }[];
};
const base = (x: RowBase) => ({ kind: x.kind, id: x.id, name: x.name, cards: n(x.cards), vehicles: n(x.vehicles), suppressed: !!x.suppressed });

export function toLift(r: LiftJson): LiftResult {
  return {
    tz: r.tz, group: r.group,
    rows: (r.rows ?? []).map((x) => ({ ...base(x), lifts: n(x.lifts), liftS: n(x.lift_s), whileDriving: n(x.while_driving), whileDrivingS: n(x.while_driving_s) })),
    totals: { lifts: n(r.totals?.lifts), liftS: n(r.totals?.lift_s), whileDriving: n(r.totals?.while_driving), whileDrivingS: n(r.totals?.while_driving_s), vehicles: n(r.totals?.vehicles), operators: n(r.totals?.operators) },
    unconfigured: (r.unconfigured ?? []).map((u) => ({ vehicleId: u.vehicle_id, fleetNo: u.fleet_no })),
  };
}

type EcoJson = {
  tz: string; group: EcoResult["group"];
  rows: (RowBase & { accel: Num | null; brake: Num | null; corner: Num | null; total: Num | null; max_g: Num | null })[];
  totals: { accel: Num; brake: Num; corner: Num; total: Num; max_g: Num | null; vehicles: Num; operators: Num };
  events: { at: string; vehicle_id: string; fleet_no: string; type: EcoEventType; g: Num | null; operator_name: string | null; lat: Num | null; lon: Num | null }[];
  truncated: boolean;
  hidden_events?: Num;
  sources: { vehicle_id: string; fleet_no: string; family: DeviceFamily | null; eco: boolean }[];
};

export function toEco(r: EcoJson): EcoResult {
  return {
    tz: r.tz, group: r.group,
    rows: (r.rows ?? []).map((x) => ({ ...base(x), accel: n(x.accel), brake: n(x.brake), corner: n(x.corner), total: n(x.total), maxG: nn(x.max_g) })),
    totals: { accel: n(r.totals?.accel), brake: n(r.totals?.brake), corner: n(r.totals?.corner), total: n(r.totals?.total), maxG: nn(r.totals?.max_g), vehicles: n(r.totals?.vehicles), operators: n(r.totals?.operators) },
    events: (r.events ?? []).map((e) => ({ at: e.at, vehicleId: e.vehicle_id, fleetNo: e.fleet_no, type: e.type, g: nn(e.g), operatorName: e.operator_name, lat: nn(e.lat), lon: nn(e.lon) })),
    truncated: !!r.truncated,
    hiddenEvents: n(r.hidden_events),
    sources: (r.sources ?? []).map((s) => ({ vehicleId: s.vehicle_id, fleetNo: s.fleet_no, family: s.family, eco: !!s.eco })),
  };
}

export const telematicsArgs = (q: TelematicsQuery) => ({ tenantId: q.tenantId, siteId: q.siteId, vehicleIds: q.vehicleIds, operatorId: q.operatorId, from: q.from, to: q.to, group: q.group });
const groupedRpc = (q: TelematicsQuery) => ({ p_tenant: q.tenantId, p_site: q.siteId, p_vehicles: q.vehicleIds.length ? q.vehicleIds : null, p_operator: q.operatorId, p_from: q.from, p_to: q.to, p_group: q.group });

export async function reportLift(db: SupabaseClient, check: Check, q: TelematicsQuery): Promise<LiftResult> {
  return toLift(check<LiftJson>(await db.rpc("report_lift", groupedRpc(q))));
}
export async function exportLift(db: SupabaseClient, check: Check, q: TelematicsQuery, format: ExportFormat): Promise<LiftResult> {
  return toLift(check<LiftJson>(await db.rpc("export_report", { p_report: "lift", p_format: format, p_args: telematicsArgs(q) })));
}
export async function reportEco(db: SupabaseClient, check: Check, q: TelematicsQuery): Promise<EcoResult> {
  return toEco(check<EcoJson>(await db.rpc("report_eco", groupedRpc(q))));
}
export async function exportEco(db: SupabaseClient, check: Check, q: TelematicsQuery, format: ExportFormat): Promise<EcoResult> {
  return toEco(check<EcoJson>(await db.rpc("export_report", { p_report: "eco", p_format: format, p_args: telematicsArgs(q) })));
}

/* ---------------- Teltonika status, immobiliser, calibration ---------------- */

type StatusJson = {
  device_id: string; has_card_reader: boolean; list_ver: Num | null; acked_ver: Num | null; acked_at: string | null; sent_at: string | null;
  in_sync: boolean; cards: Num; pending_add: Num; pending_remove: Num; slots_unknown: Num; last_sync_at: string | null;
  last_failure: { at: string; error: string | null } | null; failures: Num; retry_at: string | null; reconciled_at: string | null;
  safety_checked_at: string | null; unsafe: string[] | null;
};
export function toTeltonikaStatus(j: StatusJson): TeltonikaSyncStatus {
  return {
    deviceId: j.device_id, hasCardReader: j.has_card_reader, listVer: nn(j.list_ver), ackedVer: nn(j.acked_ver), ackedAt: j.acked_at, sentAt: j.sent_at,
    inSync: !!j.in_sync, cards: n(j.cards), pendingAdd: n(j.pending_add), pendingRemove: n(j.pending_remove), slotsUnknown: n(j.slots_unknown),
    lastSyncAt: j.last_sync_at, lastFailure: j.last_failure, failures: n(j.failures), retryAt: j.retry_at, reconciledAt: j.reconciled_at,
    safetyCheckedAt: j.safety_checked_at, unsafe: j.unsafe,
  };
}
export async function teltonikaStatus(db: SupabaseClient, check: Check, deviceId: string): Promise<TeltonikaSyncStatus> {
  return toTeltonikaStatus(check<StatusJson>(await db.rpc("teltonika_device_status", { p_device: deviceId })));
}

export async function immobilizer(db: SupabaseClient, check: Check, deviceId: string): Promise<DeviceImmobilizer> {
  const rows = check<{ immo: ImmobilizerState | null; immo_at: string | null }[]>(await db.from("device_latest").select("immo, immo_at").eq("device_id", deviceId).limit(1));
  return { state: rows[0]?.immo ?? null, at: rows[0]?.immo_at ?? null };
}

type CalRow = {
  battery_source: BatterySource; bat_scale: Num; bat_offset: Num; bat_low_v: Num | null; lift_source: LiftSource; lift_scale: Num; lift_offset: Num;
  lift_on: Num | null; lift_off: Num | null; lift_drive_kmh: Num; charge_rise_v: Num; stored: boolean; charge_v: Num | null;
};
export function toCalibration(r: CalRow): AnalogCalibrationRecord {
  return {
    batterySource: r.battery_source, batScale: n(r.bat_scale), batOffset: n(r.bat_offset), batLowV: nn(r.bat_low_v),
    liftSource: r.lift_source, liftScale: n(r.lift_scale), liftOffset: n(r.lift_offset), liftOn: nn(r.lift_on), liftOff: nn(r.lift_off),
    liftDriveKmh: n(r.lift_drive_kmh), chargeRiseV: n(r.charge_rise_v), chargeV: nn(r.charge_v), stored: !!r.stored,
  };
}
export async function analogCalibration(db: SupabaseClient, check: Check, vehicleId: string): Promise<AnalogCalibrationRecord> {
  const rows = check<CalRow[]>(await db.rpc("fts_vehicle_calibration", { p_vehicle: vehicleId }));
  return toCalibration(rows[0]!);
}
export function calibrationJson(c: VehicleAnalogCalibration): Record<string, unknown> {
  return {
    battery_source: c.batterySource, bat_scale: c.batScale, bat_offset: c.batOffset, bat_low_v: c.batLowV,
    lift_source: c.liftSource, lift_scale: c.liftScale, lift_offset: c.liftOffset, lift_on: c.liftOn, lift_off: c.liftOff,
    lift_drive_kmh: c.liftDriveKmh, charge_rise_v: c.chargeRiseV, charge_v: c.chargeV,
  };
}
export async function saveCalibration(db: SupabaseClient, check: Check, vehicleId: string, c: VehicleAnalogCalibration): Promise<void> {
  check(await db.rpc("set_vehicle_analog_calibration", { p_vehicle: vehicleId, p_cal: calibrationJson(c) }));
}
